import { FC, useEffect, useState } from 'react';
import { Box, useTheme } from '@mui/material';
import {
  Tabs as UITabs,
  Typography,
  useSnackbar,
  Loader,
} from '@fdha/web-ui-library';
import { SectionType } from '@fdha/common-utils';
import { useMenuOptionFilter } from '@fdha/common-hooks';
import {
  MealOptionType,
  MenuOptionSelectionAvailable,
} from '@fdha/graphql-api-admin';

import { useChooseMealsData } from '../../../../hooks/useMealDeliveryContext';
import { ChooseMealsMenuSection } from '../../../../components/delivery/ChooseMeals/ChooseMealsMenuSection';
import { getStyles } from '../../../../components/delivery/ChooseMeals/ChooseMeals.styles';
import ChooseMealsFooter from '../../../../components/delivery/ChooseMeals/ChooseMealsFooter';
import { DishDetails } from '../DishDetails';

interface ChooseMealsProps {
  patientId: string;
  subjectId?: string;
  deliveryMenuId: string;
  deliveryId: string;
  foodProgramUserId: string;
  itemType: SectionType;
  handleReviewSelection: () => void;
}

interface DishDetailsData {
  open: boolean;
  dishId: string;
  mealOptionType: MealOptionType;
  selectedVariantId?: string;
  selectedCount: number;
  requiredCount: number;
  menuOptionId: string;
  sectionName: SectionType;
}

const subSectionSubTitle = {
  prepared: 'Fully cooked meals - just reheat and enjoy.',
  mealKit:
    'Get a recipe, pre-cooked protein, and sauce — add your own veggies and pantry staples.',
};

const ChooseMeals: FC<ChooseMealsProps> = ({
  itemType,
  handleReviewSelection,
}) => {
  const theme = useTheme();
  const styles = getStyles(theme);
  const { showSnackbar } = useSnackbar();
  let {
    loading,
    error,
    entrees,
    snacksAndSides,
    maxEntrees,
    maxSnacksAndSides,
    selectedEntreesCount,
    selectedSnacksAndSidesCount,
    entreeDisplay,
    snacksDisplay,
  } = useChooseMealsData();

  const [dishDetailsData, setDishDetailsData] = useState<DishDetailsData>({
    open: false,
    dishId: '',
    mealOptionType: MealOptionType.Dish,
    selectedCount: 0,
    requiredCount: 1,
    sectionName: 'entreeDishes',
    menuOptionId: '',
  });

  const filteredEntrees = useMenuOptionFilter(entrees);
  const filteredSnacks = useMenuOptionFilter(snacksAndSides);

  // Show error via snackbar
  useEffect(() => {
    if (error) {
      showSnackbar({
        message: `Error loading menu options: ${error}`,
        severity: 'error',
      });
    }
  }, [error, showSnackbar]);

  if (loading) {
    return <Loader />;
  }

  const handleItemClick = (
    item: MenuOptionSelectionAvailable,
    sectionType: SectionType
  ) => {
    // Determine if it's an entree or snack to get the right counts
    const isEntree = sectionType === 'entreeDishes';
    const currentDisplay = isEntree ? entreeDisplay : snacksDisplay;

    setDishDetailsData({
      open: true,
      dishId: item.dishId,
      mealOptionType: item.mealOptionType as MealOptionType,
      selectedCount: currentDisplay.selected,
      requiredCount: currentDisplay.total,
      menuOptionId: item.menuOptionId,
      sectionName: sectionType,
    });
  };

  const handleCloseDishDetails = () => {
    setDishDetailsData((prev) => ({ ...prev, open: false }));
  };

  const tabs = [
    {
      key: 'entreeDishes',
      title: 'Entrees',
    },
    {
      key: 'snacks',
      title: 'Snacks/Sides',
    },
  ];

  const EntreesContent = (
    <Box>
      <ChooseMealsMenuSection
        key="entrees-section"
        title="Choose Entree dishes"
        sectionType={'entreeDishes'}
        filterState={filteredEntrees}
        selectedCount={selectedEntreesCount}
        requiredCount={maxEntrees}
        subSectionSubTitle={subSectionSubTitle}
        onItemClick={handleItemClick}
      />
    </Box>
  );

  const SnacksContent = (
    <Box>
      {maxSnacksAndSides > 0 ? (
        <ChooseMealsMenuSection
          key="snacks-section"
          title="Choose Snacks/Sides"
          sectionType={'snacks'}
          filterState={filteredSnacks}
          selectedCount={selectedSnacksAndSidesCount}
          requiredCount={maxSnacksAndSides}
          subSectionSubTitle={subSectionSubTitle}
          onItemClick={handleItemClick}
        />
      ) : (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No snacks required for this delivery.
          </Typography>
        </Box>
      )}
    </Box>
  );

  const tabContent = {
    entreeDishes: EntreesContent,
    snacks: SnacksContent,
  };

  // Determine initial index based on itemType
  const initialIndex = itemType === 'entreeDishes' ? 0 : 1;

  const selectedDishItem = {
    dishId: dishDetailsData.dishId,
    mealOptionType: dishDetailsData.mealOptionType,
    menuOptionId: dishDetailsData.menuOptionId,
    selectedVariantId: dishDetailsData.selectedVariantId,
    sectionName: dishDetailsData.sectionName,
  };

  return (
    <Box style={styles.mainContainer}>
      <UITabs
        tabs={tabs}
        tabContent={tabContent}
        initialIndex={initialIndex}
        indicatorPosition="top"
        variant="fullWidth"
      />

      {/* Footer with summary and actions */}
      <ChooseMealsFooter
        entreesSelected={entreeDisplay.selected}
        entreesTotal={entreeDisplay.total}
        snacksSelected={snacksDisplay.selected}
        snacksTotal={snacksDisplay.total}
        handleReviewSelection={handleReviewSelection}
      />

      {/* Dish Details Modal */}
      {dishDetailsData.open && (
        <DishDetails
          dishItem={selectedDishItem}
          selectedCount={dishDetailsData.selectedCount}
          requiredCount={dishDetailsData.requiredCount}
          onClose={handleCloseDishDetails}
          isPreviewMode={false}
          open={dishDetailsData.open}
        />
      )}
    </Box>
  );
};

export default ChooseMeals;
